import exportFromJSON from "export-from-json";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { deleteProduct, getAllProducts, updateProductSequence } from "../../../../api/productAPIs";
import {
  BlockBetween,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PreviewCard,
  ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sequenceInput, setSequenceInput] = useState(0);

  const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);
  const toggleSequenceModal = () => setIsSequenceModalOpen(!isSequenceModalOpen);

  const handleDelete = async (product) => {
    try {
      await deleteProduct({ productId: product._id });
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
      toast.success("Product deleted successfully", { position: "top-center" });
      toggleDeleteModal();
    } catch (error) {
      toast.error(`Failed to delete product: ${error.message}`, { position: "top-center" });
    }
  };

  const handleProductSequence = async (productId, sequence) => {
    try {
      await updateProductSequence({
        formData: {
          sequence: Number(sequence),
        },
        productId,
      });
      setProducts((prev) =>
        prev.map((p) => (p._id === productId ? { ...p, sequence: Number(sequence) } : p))
      );
      toast.success("Product sequence updated successfully", { position: "top-center" });
      toggleSequenceModal();
    } catch (error) {
      toast.error(`Failed to update product sequence: ${error.message}`, { position: "top-center" });
    }
  };

  const openSequenceModal = (product) => {
    setSelectedProduct(product);
    setSequenceInput(product.sequence || 0);
    toggleSequenceModal();
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getAllProducts();
        const fetchedProducts = response.data.data.products;
        setProducts(fetchedProducts?.reverse());
      } catch (error) {
        console.error("Failed to fetch products:", error);
        toast.error("Failed to fetch products", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const exportData = (type) => {
    const fileName = "products";
    const exportType = type === "csv" ? exportFromJSON.types.csv : exportFromJSON.types.xls;
    const exportData = products.map((product) => ({
      Name: product.name,
      Category: product.category_id?.name || "N/A",
      Subcategory: product.subcategory_id?.name || "N/A",
      Price: parseFloat(product.variants[0]?.price?.$numberDecimal || "0").toFixed(2),
      Stock: product.variants.reduce((sum, v) => sum + (v.stock || 0), 0),
      Discount: product.variants.some((v) => v.discount?.active) ? "Active" : "None",
      Sequence: product.sequence || 0,
    }));
    exportFromJSON({ data: exportData, fileName, exportType });
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="d-flex align-items-center m-1 gap-2" style={{ cursor: "pointer" }}>
          <img
            src={row.images?.[0]?.url || "https://via.placeholder.com/50"}
            alt={row.name}
            className="img-fluid rounded py-1"
            style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "cover" }}
            onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/50")}
          />
          <div
            className="fw-bold text-truncate"
            style={{ cursor: "pointer", width: "80px" }}
            onClick={() => navigate(`/edit-product/${row._id}`)}
          >
            {row.name}
          </div>
        </div>
      ),
    },
    {
      name: "Category",
      selector: (row) => row.category_id?.name || "N/A",
      cell: (row) => <span className="text-muted">{row.category_id?.name || "N/A"}</span>,
      sortable: true,
      hide: "md",
    },
    {
      name: "Subcategory",
      selector: (row) => row.subcategory_id?.name || "N/A",
      cell: (row) => <span className="text-muted">{row.subcategory_id?.name || "N/A"}</span>,
      sortable: true,
      hide: "md",
    },
    {
      name: "Price",
      selector: (row) => row.variants[0]?.price?.$numberDecimal || "0",
      cell: (row) => (
        <span className="fw-bold text-dark">
          ₹{parseFloat(row.variants[0]?.price?.$numberDecimal || "0").toFixed(2)}
          {row.variants.length > 1 && ` (${row.variants.length} variants)`}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Stock",
      selector: (row) => row.variants[0]?.stock || 0,
      cell: (row) => {
        const totalStock = row.variants.reduce((sum, variant) => sum + (variant.stock || 0), 0);
        return (
          <span
            className={`badge rounded-pill ${totalStock > 0 ? "bg-success text-white" : "bg-danger text-white"}`}
          >
            {totalStock > 0 ? `In Stock (${totalStock})` : "Out of Stock"}
          </span>
        );
      },
      sortable: true,
    },
    {
      name: "Discount",
      selector: (row) => row.variants.some((v) => v.discount?.active),
      cell: (row) => {
        const hasActiveDiscount = row.variants.some((v) => v.discount?.active);
        return (
          <span
            className={`badge rounded-pill ${hasActiveDiscount ? "bg-info text-white" : "bg-secondary text-white"}`}
          >
            {hasActiveDiscount ? "Active" : "None"}
          </span>
        );
      },
      sortable: true,
      hide: "lg",
    },
    {
      name: "Sequence",
      selector: (row) => row.sequence || 0,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <span>{row.sequence || 0}</span>
          <Button
            size="sm"
            color="outline-primary"
            onClick={() => openSequenceModal(row)}
          >
            <Icon name="sort"></Icon>

          </Button>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            color="outline-primary"
            onClick={() => navigate(`/edit-product/${row._id}`)}
          >
            <i className="bi bi-pencil-fill me-1"></i>
            <span className="d-none d-md-inline">Edit</span>
          </Button>
          <Button
            size="sm"
            color="outline-danger"
            onClick={() => {
              setSelectedProduct(row);
              toggleDeleteModal();
            }}
          >
            <i className="bi bi-trash-fill me-1"></i>
            <span className="d-none d-md-inline">Delete</span>
          </Button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  const expandableRowsComponent = ({ data }) => (
    <div className="p-2 bg-light">
      <Row>
        <Col xs="12" className="mb-2">
          <strong>Category:</strong> {data.category_id?.name || "N/A"}
        </Col>
        <Col xs="12" className="mb-2">
          <strong>Subcategory:</strong> {data.subcategory_id?.name || "N/A"}
        </Col>
        <Col xs="12" className="mb-2">
          <strong>Discount:</strong>{" "}
          {data.variants.some((v) => v.discount?.active) ? "Active" : "None"}
        </Col>
        <Col xs="12">
          <strong>Sequence:</strong> {data.sequence || 0}
        </Col>
      </Row>
    </div>
  );

  return (
    <React.Fragment>
      <Head title="Product List" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Product List
              </BlockTitle>
              <BlockDes className="text-muted">
                <p>You have a total of {products.length} products.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Row className="g-2">
                <Col>
                  <Button color="secondary" outline onClick={() => exportData("csv")}>
                    <i className="bi bi-file-earmark-text me-1"></i>
                    <span className="d-none d-md-inline">Export CSV</span>
                  </Button>
                </Col>
                <Col>
                  <Button color="secondary" outline onClick={() => exportData("xls")}>
                    <i className="bi bi-file-earmark-excel me-1"></i>
                    <span className="d-none d-md-inline">Export Excel</span>
                  </Button>
                </Col>
                <Col>
                  <Button color="success" onClick={() => navigate("/add-product")}>
                    <i className="bi bi-plus-lg me-1"></i>
                    <span className="d-none d-md-inline">Add Product</span>
                  </Button>
                </Col>
              </Row>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BlockContent>
          <PreviewCard>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <div className="text-center">
                  <div
                    className="spinner-grow text-primary"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading products, please wait...</p>
                </div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="text-muted">No products found</h5>
                <Button color="primary" onClick={() => navigate("/add-product")}>
                  Add Your First Product
                </Button>
              </div>
            ) : (
              <ReactDataTable
                data={products}
                columns={columns}
                pagination
                highlightOnHover
                striped
                dense
                expandableRows
                expandableRowsComponent={expandableRowsComponent}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                className="table-responsive"
              />
            )}
          </PreviewCard>
        </BlockContent>
      </Content>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        toggle={toggleDeleteModal}
        onConfirm={handleDelete}
        item={selectedProduct}
        itemName={selectedProduct?.name}
      />
      <Modal isOpen={isSequenceModalOpen} toggle={toggleSequenceModal} centered>
        <ModalHeader toggle={toggleSequenceModal}>
          Update Product Sequence
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="sequenceInput">Sequence Number</Label>
            <Input
              type="number"
              id="sequenceInput"
              value={sequenceInput}
              min="0"
              onChange={(e) => setSequenceInput(e.target.value)}
              className="form-control-lg"
              placeholder="Enter sequence number"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleSequenceModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => handleProductSequence(selectedProduct?._id, sequenceInput)}
            disabled={sequenceInput < 0}
          >
            Update
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ProductList;